import { listCultivarsQueryOptions } from "@packages/api-client/hooks/cultivarController";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus, Search, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { DashboardSkeleton } from "@/components/ui/skeleton";
import { requireReachableRoute } from "@/lib/offline/routeGuard";
import { CultivarList } from "./-components/CultivarList";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/grass-production/cultivars/",
)({
	// Cultivar management is desk work — online-only by design.
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	component: CultivarsPage,
});

function CultivarsPage() {
	const { orgId } = Route.useParams();
	const { farm } = Route.useSearch();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();
	const [search, setSearch] = useState("");
	const [source, setSource] = useState<"all" | "system" | "organization">(
		"all",
	);

	const { data, isLoading, error, refetch } = useQuery(
		listCultivarsQueryOptions(orgId),
	);
	const cultivars = data?.data ?? [];
	const normalizeSearch = (value: string) =>
		value
			.normalize("NFD")
			.replace(/[\u0300-\u036f]/g, "")
			.toLocaleLowerCase();
	const normalizedSearch = normalizeSearch(search.trim());
	const filteredCultivars = cultivars.filter((cultivar) => {
		if (source === "system" && !cultivar.isSystemCultivar) return false;
		if (source === "organization" && cultivar.isSystemCultivar) return false;
		return [cultivar.commonName, cultivar.species, cultivar.variety].some(
			(value) => value && normalizeSearch(value).includes(normalizedSearch),
		);
	});
	const hasFilters = normalizedSearch.length > 0 || source !== "all";

	if (isLoading) {
		return <DashboardSkeleton />;
	}

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title={t("grass.cultivar.list.title")}
				showBackButton
				inlineActionsOnMobile
				actions={
					<Button
						asChild
						size="sm"
						className="h-7 gap-1 px-2 text-[12px] sm:h-9 sm:gap-2 sm:px-4 sm:text-sm"
					>
						<Link
							to="/app/org/$orgId/grass-production/cultivars/new"
							params={{ orgId }}
							search={{ farm }}
						>
							<Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
							<span className="sm:hidden">
								{t("grass.cultivar.actions.createShort")}
							</span>
							<span className="hidden sm:inline">
								{t("grass.cultivar.actions.create")}
							</span>
						</Link>
					</Button>
				}
			/>
			<p className="max-w-3xl text-sm text-base-content/70">
				{t("grass.cultivar.list.description")}
			</p>
			{cultivars.length > 0 && (
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
					<div className="relative w-full sm:max-w-sm">
						<Search
							aria-hidden="true"
							className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-base-content/50"
						/>
						<input
							type="search"
							value={search}
							onChange={(event) => setSearch(event.target.value)}
							placeholder={t("grass.cultivar.list.searchPlaceholder")}
							aria-label={t("grass.cultivar.list.searchPlaceholder")}
							className="h-11 w-full rounded-xl border border-base-300 bg-base-100 pr-4 pl-10 text-sm text-base-content outline-none transition-colors placeholder:text-base-content/50 focus:border-primary focus:ring-2 focus:ring-primary/20"
						/>
					</div>
					<fieldset className="flex w-full gap-1 overflow-x-auto rounded-xl bg-base-200/70 p-1 sm:w-auto">
						<legend className="sr-only">
							{t("grass.cultivar.list.sourceFilter")}
						</legend>
						{(["all", "organization", "system"] as const).map((option) => (
							<button
								key={option}
								type="button"
								onClick={() => setSource(option)}
								aria-pressed={source === option}
								className={`shrink-0 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${source === option ? "bg-base-100 text-base-content shadow-sm" : "text-base-content/65 hover:text-base-content"}`}
							>
								{t(`grass.cultivar.list.filters.${option}`)}
							</button>
						))}
					</fieldset>
				</div>
			)}
			{cultivars.length > 0 && (
				<output className="-mt-3 text-sm text-base-content/60">
					{t("grass.cultivar.list.resultCount", {
						count: filteredCultivars.length,
						total: cultivars.length,
					})}
				</output>
			)}
			{hasFilters && filteredCultivars.length === 0 && !error ? (
				<div className="flex flex-col items-center rounded-2xl border border-base-300 bg-base-100 px-6 py-14 text-center shadow-sm">
					<Search
						aria-hidden="true"
						className="mb-3 h-8 w-8 text-base-content/35"
					/>
					<h2 className="font-display text-lg font-bold">
						{t("grass.cultivar.list.noResults")}
					</h2>
					<p className="mt-1 max-w-sm text-sm text-base-content/65">
						{t("grass.cultivar.list.noResultsDescription")}
					</p>
					<Button
						variant="outline"
						className="mt-5"
						onClick={() => {
							setSearch("");
							setSource("all");
						}}
					>
						<X aria-hidden="true" className="mr-2 h-4 w-4" />
						{t("grass.cultivar.list.clearFilters")}
					</Button>
				</div>
			) : (
				<CultivarList
					cultivars={filteredCultivars}
					isLoading={isLoading ?? false}
					error={error}
					onRetry={() => void refetch()}
					onCreateFromSystem={(from) =>
						void navigate({
							to: "/app/org/$orgId/grass-production/cultivars/new",
							params: { orgId },
							search: { farm, from },
						})
					}
					onEdit={(cultivarId) =>
						void navigate({
							to: "/app/org/$orgId/grass-production/cultivars/$cultivarId/edit",
							params: { orgId, cultivarId },
							search: { farm },
						})
					}
				/>
			)}
		</div>
	);
}
